import { FieldValues, Path, UseFormSetError } from 'react-hook-form';

export interface ServerError {
  message: string;
  errors?: Record<string, string[]>;
}

export interface RTKQueryError {
  status: number;
  data?: ServerError;
}

/**
 * Handles server-side validation errors and maps them to React Hook Form field errors
 * @param error - The error from RTK Query
 * @param setError - React Hook Form's setError function
 * @returns Whether any field errors were set
 */
export function handleFormErrors<T extends FieldValues>(
  error: unknown,
  setError: UseFormSetError<T>
): boolean {
  // Check if it's an RTK Query error with validation data
  if (
    error &&
    typeof error === 'object' &&
    'status' in error &&
    'data' in error
  ) {
    const rtqError = error as RTKQueryError;
    
    if (rtqError.data?.errors) {
      let hasFieldErrors = false;
      
      // Map server field errors to form field errors
      Object.entries(rtqError.data.errors).forEach(([field, messages]) => {
        if (messages && messages.length > 0) {
          // Use the first error message for the field
          setError(field as Path<T>, {
            type: 'server',
            message: messages[0]
          });
          hasFieldErrors = true;
        }
      });
      
      // If no field-specific errors, set a root error with the general message
      if (!hasFieldErrors && rtqError.data.message) {
        setError('root', {
          type: 'server',
          message: rtqError.data.message
        });
      }
      
      return hasFieldErrors;
    }
    
    // Handle cases where there's a general message but no field errors
    if (rtqError.data?.message) {
      setError('root', {
        type: 'server',
        message: rtqError.data.message
      });
      return false;
    }
  }
  
  // Fallback for unknown error types
  setError('root', {
    type: 'server',
    message: 'An unexpected error occurred. Please try again.'
  });
  
  return false;
}

/**
 * Error code mappings for user-friendly messages
 * Add your specific error codes here
 */
export const ERROR_CODE_MESSAGES: Record<string, string> = {
  'CONDITION-LOOKUP-GROUP-0004': 'This group name already exists. Please choose a different name.',
  'SPECIALITY-0001': 'This speciality title already exists. Please choose a different title.',
  // Add more error codes as needed
};

/**
 * Converts error codes to user-friendly messages
 * @param errorCode - The error code from the server
 * @returns User-friendly error message
 */
export function getErrorMessage(errorCode: string): string {
  return ERROR_CODE_MESSAGES[errorCode] || errorCode;
} 